import { CommonModule } from "@angular/common";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { form, FormField, validate } from "@angular/forms/signals";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { GenerateReportService } from "@shared/services/generate-report.service";
import { CustomHeaderService } from "app/layout/common/custom-header-title.service";
import { DateTime } from "luxon";

interface ReportFilters {
  fromDate: DateTime | null;
  toDate: DateTime | null;
}

interface Preset {
  key: string;
  label: string;
}

type DownloadStatus = "idle" | "working" | "done";

@Component({
  selector: "app-generate-report",
  imports: [CommonModule, FormField, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatDatepickerModule],
  templateUrl: "./generate-report.component.html",
  styleUrl: "./generate-report.component.scss",
})
export class GenerateReportComponent implements OnInit, OnDestroy {
  private reportService = inject(GenerateReportService);
  private _customHeaderService = inject(CustomHeaderService);

  readonly today = DateTime.now().startOf("day");

  readonly presets: Preset[] = [
    { key: "week", label: "Last 7 days" },
    { key: "thirty", label: "Last 30 days" },
    { key: "month", label: "This month" },
    { key: "lastmonth", label: "Last month" },
  ];

  readonly filters = signal<ReportFilters>({ fromDate: null, toDate: null });

  // Both dates are optional
  readonly reportForm = form(this.filters, (path) => {
    validate(path.toDate, ({ value, valueOf }) => {
      const fromDate = valueOf(path.fromDate);
      const toDate = value();
      if (fromDate && toDate && toDate < fromDate) {
        return { kind: "dateRange", message: "The From date must be on or before the To date." };
      }
      return null;
    });
  });

  readonly status = signal<DownloadStatus>("idle");
  readonly downloading = computed(() => this.status() === "working");
  readonly errorMessage = signal<string | null>(null);

  readonly activePreset = computed<string | null>(() => {
    const { fromDate, toDate } = this.filters();
    if (!fromDate || !toDate) {
      return null;
    }
    return this.presets.find((preset) => {
      const range = this.presetRange(preset.key);
      return range.from.hasSame(fromDate, "day") && range.to.hasSame(toDate, "day");
    })?.key ?? null;
  });

  readonly rangeError = computed<string | null>(() => this.reportForm.toDate().errors()[0]?.message ?? null);

  readonly hint = computed<string>(() => {
    const rangeError = this.rangeError();
    if (rangeError) {
      return rangeError;
    }
    if (this.status() === "done") {
      return "Export ready - check your downloads.";
    }

    const { fromDate, toDate } = this.filters();
    if (fromDate && toDate) {
      const days = Math.round(toDate.diff(fromDate, "days").days) + 1;
      return `${this.pretty(fromDate)} to ${this.pretty(toDate)} · ${days} day${days === 1 ? "" : "s"} of transactions.`;
    }
    if (fromDate) {
      return `From ${this.pretty(fromDate)} onwards.`;
    }
    if (toDate) {
      return `Up to ${this.pretty(toDate)}.`;
    }
    return "No range selected - only today's transactions will be generated.";
  });

  readonly hintIcon = computed<string>(() => {
    if (this.rangeError()) {
      return "heroicons_outline:exclamation-triangle";
    }
    if (this.status() === "done") {
      return "heroicons_outline:check-circle";
    }
    const { fromDate, toDate } = this.filters();
    return fromDate || toDate ? "heroicons_outline:clock" : "heroicons_outline:information-circle";
  });

  readonly hintClass = computed<string>(() => {
    if (this.rangeError()) {
      return "text-rose-600";
    }
    if (this.status() === "done") {
      return "text-emerald-700";
    }
    const { fromDate, toDate } = this.filters();
    return fromDate || toDate ? "text-slate-500" : "text-slate-400";
  });

  private doneTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this._customHeaderService.setTitle("Report");
  }

  ngOnDestroy(): void {
    clearTimeout(this.doneTimer);
  }

  applyPreset(preset: Preset): void {
    const { from, to } = this.presetRange(preset.key);
    this.filters.set({ fromDate: from, toDate: to });
    this.resetStatus();
  }

  clearDates(): void {
    this.filters.set({ fromDate: null, toDate: null });
    this.resetStatus();
  }

  download(): void {
    this.reportForm().markAsTouched();
    if (this.reportForm().invalid() || this.downloading()) {
      return;
    }

    this.errorMessage.set(null);
    this.status.set("working");

    this.reportService.downloadFeeCollectionReport(this.dateFormat("fromDate"), this.dateFormat("toDate")).subscribe({
      next: (response) => {
        this.saveFile(response);
        this.flagDone();
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage.set(error.status === 0 ? "Unable to reach the server. Please try again." : "Report could not be generated. Please try again.");
        this.status.set("idle");
      },
    });
  }

  private flagDone(): void {
    this.status.set("done");
    clearTimeout(this.doneTimer);
    this.doneTimer = setTimeout(() => this.resetStatus(), 2600);
  }

  private resetStatus(): void {
    clearTimeout(this.doneTimer);
    this.status.set("idle");
  }

  private presetRange(key: string): { from: DateTime; to: DateTime } {
    const today = DateTime.now().startOf("day");
    switch (key) {
      case "week":
        return { from: today.minus({ days: 6 }), to: today };
      case "thirty":
        return { from: today.minus({ days: 29 }), to: today };
      case "month":
        return { from: today.startOf("month"), to: today };
      default: {
        const lastMonth = today.minus({ months: 1 });
        return { from: lastMonth.startOf("month"), to: lastMonth.endOf("month").startOf("day") };
      }
    }
  }

  private pretty(date: DateTime): string {
    return date.toFormat("dd LLL yyyy");
  }

  // The backend expects ISO dates (@DateTimeFormat ISO.DATE) or nothing at all when unset.
  private dateFormat(key: keyof ReportFilters): string | null {
    return this.filters()[key]?.toISODate() ?? null;
  }

  private saveFile(response: HttpResponse<Blob>): void {
    const blob = response.body as Blob;
    const blobUrl = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = this.resolveFileName(response);
    anchor.click();

    URL.revokeObjectURL(blobUrl);
  }

  // The filename is sent on Content-Disposition; fall back to a date-stamped name when the header isn't readable
  private resolveFileName(response: HttpResponse<Blob>): string {
    const contentDisposition = response.headers.get("Content-Disposition");
    const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
    if (match) {
      return match[1];
    }
    const from = this.dateFormat("fromDate");
    const to = this.dateFormat("toDate");
    const range = from && to ? `_${from}_to_${to}` : from ? `_from_${from}` : to ? `_upto_${to}` : "";
    return `digitap-payment-report${range}.xlsx`;
  }
}
